import React from 'react';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Manufacturer {
  id: number;
  name: string;
  logo: string | null;
  logo_url: string | null;
  is_residential: boolean;
  is_commercial: boolean;
  is_other: boolean;
}

interface ManufacturerSelectorProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

export const ManufacturerSelector: React.FC<ManufacturerSelectorProps> = ({
  selectedIds,
  onChange,
}) => {
  const { data: manufacturers, isLoading, error } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const response = await api.get<{ count: number; results: Manufacturer[] } | Manufacturer[]>('/api/manufacturers/');
      const data = response.data;
      // Handle both paginated and non-paginated responses
      if (Array.isArray(data)) {
        return data;
      } else if (data?.results && Array.isArray(data.results)) {
        return data.results;
      }
      return [];
    },
  });

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" variant="soft">
        Failed to load manufacturers
      </Alert>
    );
  }

  // Group manufacturers by category
  const residential = manufacturers?.filter((m) => m.is_residential) || [];
  const commercial = manufacturers?.filter((m) => m.is_commercial) || [];
  const other = manufacturers?.filter((m) => m.is_other) || [];

  const renderCategory = (title: string, items: Manufacturer[]) => {
    if (items.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 1.5, color: 'neutral.600' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {items.map((manufacturer) => {
            const isSelected = selectedIds.includes(manufacturer.id);
            return (
              <Chip
                key={manufacturer.id}
                variant={isSelected ? 'solid' : 'outlined'}
                color={isSelected ? 'primary' : 'neutral'}
                onClick={() => handleToggle(manufacturer.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? 'primary.600'
                      : 'neutral.100',
                  },
                }}
              >
                {manufacturer.name}
              </Chip>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {renderCategory('Residential Roofing', residential)}
      {renderCategory('Commercial Roofing', commercial)}
      {renderCategory('Other (Siding, Windows, Gutters)', other)}
      {selectedIds.length > 0 && (
        <Typography level="body-sm" sx={{ mt: 2, color: 'neutral.500' }}>
          {selectedIds.length} manufacturer{selectedIds.length !== 1 ? 's' : ''} selected
        </Typography>
      )}
    </Box>
  );
};

export default ManufacturerSelector;
